import { fold } from './searchTerms';
import { MANUAL_MAILBOX, mailboxLabel } from './types';

/**
 * What a row of the inbox says about where an email comes from and where it arrived.
 *
 * Until 2026-09-23 the grey line under the sender's name was the mailbox the email came
 * in through, and it read as the sender's address: «Notificación judicial» over
 * «compras@ferreteriacubas.es» (Eloy). «De» is now the sender, name and address, and the
 * mailbox has its own column.
 */

/** The sender as two lines: the name, and the address under it when there is a name. */
export interface SenderLines {
  /** The first line: the name, or the address when there is no name. */
  primary: string;
  /** The address under the name, or null when the first line already is the address. */
  secondary: string | null;
}

/**
 * `from_name` over `from_address`. A name that is empty, or is the address again (plenty
 * of senders put the address in the display name), is no name: the row would say the
 * same thing twice.
 */
export function senderLines(email: {
  from_name?: string | null | undefined;
  from_address: string;
}): SenderLines {
  const address = email.from_address.trim();
  const name = email.from_name?.trim() ?? '';
  if (!name || fold(name) === fold(address)) {
    return { primary: address || '(sin remitente)', secondary: null };
  }
  return { primary: name, secondary: address || null };
}

/** The domain of a real mailbox, lowercase, or null for `manual` and anything odd. */
function domainOf(mailbox: string): string | null {
  if (mailbox === MANUAL_MAILBOX) return null;
  const at = mailbox.lastIndexOf('@');
  if (at <= 0 || at === mailbox.length - 1) return null;
  return mailbox.slice(at + 1).toLowerCase();
}

/**
 * The mailbox as the «Buzón» column says it. When every mailbox the person can see is on
 * the same domain, the part before the @ is enough and fits the column: «compras»,
 * «ventas». With two domains it is the whole address, or «compras» would not say which.
 * The one that is not a mailbox, the uploads, says «Subidos a mano» as everywhere else.
 *
 * `known` is `GET /api/emails/mailboxes`, or the mailboxes of the page while that list
 * has not arrived. A mailbox missing from it is shortened only if it is on that same
 * one domain; with nothing known, nothing is shortened.
 */
export function mailboxShortLabel(mailbox: string, known: readonly string[]): string {
  const domain = domainOf(mailbox);
  if (domain === null) return mailboxLabel(mailbox);
  const domains = new Set(known.map(domainOf).filter((d): d is string => d !== null));
  const shared = domains.size === 1 && domains.has(domain);
  return shared ? mailbox.slice(0, mailbox.lastIndexOf('@')) : mailbox;
}
